import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Space, Popconfirm, Avatar, Spin, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { teamAPI } from '../services/api';

const { Title } = Typography;

export default function Team() {
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(null);

    useEffect(() => {
        fetchTeam();
    }, []);

    const fetchTeam = async () => {
        try {
            setLoading(true);
            const response = await teamAPI.getAll();
            setTeam(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching team:', error);
            message.error('Failed to fetch team');
            setTeam([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setDeleteLoading(id);
            await teamAPI.delete(id);
            setTeam(team.filter((t) => (t.id || t._id) !== id));
            message.success('Team member deleted successfully');
        } catch (error) {
            console.error('Error deleting team member:', error);
            message.error('Failed to delete team member');
        } finally {
            setDeleteLoading(null);
        }
    };

    const columns = [
        {
            title: 'Photo',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 80,
            render: (imageUrl) => (
                <Avatar
                    src={imageUrl}
                    icon={<UserOutlined />}
                    size={50}
                />
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Position',
            dataIndex: 'position',
            key: 'position',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Order',
            dataIndex: 'order',
            key: 'order',
            width: 80,
            sorter: (a, b) => a.order - b.order,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Link to={`/team/edit/${record.id || record._id}`}>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            size="small"
                        >
                            Edit
                        </Button>
                    </Link>
                    <Popconfirm
                        title="Are you sure you want to delete this team member?"
                        onConfirm={() => handleDelete(record.id || record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            loading={deleteLoading === (record.id || record._id)}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading team..." />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>Team Management</Title>
                <Link to="/team/new">
                    <Button type="primary" icon={<PlusOutlined />} size="large">
                        Add Team Member
                    </Button>
                </Link>
            </div>

            <Table
                columns={columns}
                dataSource={team}
                rowKey={(record) => record.id || record._id}
                pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} team members`,
                }}
                locale={{
                    emptyText: 'No team members founds',
                }}
            />
        </div>
    );
}
